import {
  AccountProductEnum,
  ButtonType,
  CancelV2ATransferResultError,
  ChargeCardTransaction,
  PayOverTimeStatus,
  RdcTransaction,
  TransactionType,
  V2ATransaction,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { useCancelTransaction } from 'features/accounts/hooks/useCancelTransaction';
import useGetTransactionDetails from 'features/accounts/hooks/useGetTransactionDetails';
import RdcTransactionDetails from 'features/accounts/TransactionDetails/RdcTransactionDetails';
import TransactionItems from 'features/accounts/TransactionDetails/TransactionItems';
import PayOverTimeApplyNowBottomSheet from 'features/payOverTime/components/PayOverTimeApplyNowBottomSheet/PayOverTimeApplyNowBottomSheet';
import PayOverTimeListItem from 'features/payOverTime/components/PayOverTimeListItem/PayOverTimeListItem';
import {
  buildErrorModel,
  ErrorModel,
  getPayOverTimeState,
  logPayOverTimeError,
  resolveErrorCopy,
} from 'features/payOverTime/helpers/PayOverTime.helpers';
import usePayOverTimeApplyNowSheet from 'features/payOverTime/hooks/usePayOverTimeApplyNowSheet';
import usePayOverTimeIntroDetails, {
  PayOverTimeIntroStatus,
} from 'features/payOverTime/hooks/usePayOverTimeIntroDetails';
import useSubmitPayOverTimeOnboardingStep, {
  PayOverTimeOnboardingStatus,
} from 'features/payOverTime/hooks/useSubmitPayOverTimeOnboardingStep';
import { setPayOverTimePaymentPlanStateAtom } from 'features/payOverTime/store/payOverTimeOnboardingStore';
import { useSetAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import Button from 'shared/components/vxl/Button/Button';
import Dialog from 'shared/components/vxl/Dialog/Dialog';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

interface TransactionDetailsErrorButton {
  title: string;
  onPress: () => void;
}

interface TransactionDetailsErrorConfig {
  error: unknown;
  messageHeadline: string;
  messageBody: string;
  primaryButton: TransactionDetailsErrorButton;
}

const logUnexpectedPayOverTimeState = (
  message: string,
  additionalContext: Record<string, unknown>,
) => {
  logPayOverTimeError(new Error(message), additionalContext);
};

const TransactionDetails = () => {
  const { transactionId, accountType } = useLocalSearchParams<{
    transactionId: string;
    accountType: string;
  }>();
  const router = useRouter();
  const dismissToTab = useDismissToRootTab();
  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'transactionDetails',
  });
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const {
    cancelTransaction,
    cancelling,
    transactionCancelled,
    cancelTransactionError,
  } = useCancelTransaction();

  const { transactionR2, loading, error, reload } =
    useGetTransactionDetails(transactionId);

  const payOverTime = getPayOverTimeState(transactionR2);
  const payOverTimeStatus = payOverTime?.status;

  const { fetchIntroDetails, fetching: fetchingIntroDetails } =
    usePayOverTimeIntroDetails();
  const [payOverTimeError, setPayOverTimeError] = useState<ErrorModel | null>(
    null,
  );

  const dismissPayOverTimeError = () => setPayOverTimeError(null);

  const {
    submitOnboardingStep,
    cancelOnboardingStep,
    fetching: fetchingSubmitStatus,
  } = useSubmitPayOverTimeOnboardingStep();
  const [submitErrorModel, setSubmitErrorModel] = useState<ErrorModel | null>(
    null,
  );

  const dismissSubmitError = () => setSubmitErrorModel(null);

  const setPayOverTimePaymentPlanState = useSetAtom(
    setPayOverTimePaymentPlanStateAtom,
  );

  const {
    bottomSheetRef,
    present: presentApplyNowSheet,
    dismiss: dismissApplyNowSheet,
    onIsOpenChange: handleApplyNowSheetOpenChange,
  } = usePayOverTimeApplyNowSheet();

  const handleApplyNowSheetIsOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      cancelOnboardingStep();
    }

    handleApplyNowSheetOpenChange(isOpen);
  };

  const handleUnexpectedPayOverTimeState = (
    logMessage: string,
    unexpectedStatus: PayOverTimeStatus | undefined,
    customerError: ErrorModel,
  ) => {
    logUnexpectedPayOverTimeState(logMessage, {
      transactionId,
      status: unexpectedStatus,
    });
    setPayOverTimeError(customerError);
  };

  const handlePayOverTimePress = async () => {
    if (!payOverTime) {
      return;
    }

    setPayOverTimeError(null);
    setSubmitErrorModel(null);

    const { status, loanId } = payOverTime;

    switch (status) {
      case PayOverTimeStatus.Eligible: {
        const outcome = await fetchIntroDetails(transactionId);

        if (outcome.status === PayOverTimeIntroStatus.Details) {
          router.push({
            pathname: '/pay-over-time/apply-now',
            params: { transactionId },
          });
          return;
        }

        if (outcome.status === PayOverTimeIntroStatus.Summary) {
          presentApplyNowSheet();
          return;
        }

        if (outcome.status === PayOverTimeIntroStatus.Error) {
          setPayOverTimeError(
            buildErrorModel(
              resolveErrorCopy(outcome.title, defaultTitle),
              resolveErrorCopy(outcome.message, defaultMessage),
            ),
          );
        }

        return;
      }

      case PayOverTimeStatus.Active:
        if (loanId) {
          router.push({
            pathname: '/pay-over-time/loan-details',
            params: { loanId },
          });
          return;
        }

        handleUnexpectedPayOverTimeState(
          'Pay Over Time transaction is active without a loanId',
          status,
          buildErrorModel(
            translatedText('payOverTimeErrors.unavailableTitle'),
            translatedText('payOverTimeErrors.activeBody'),
          ),
        );
        return;

      case PayOverTimeStatus.Closed:
        if (loanId) {
          router.push({
            pathname: '/pay-over-time/history-details',
            params: { loanId },
          });
          return;
        }

        handleUnexpectedPayOverTimeState(
          'Pay Over Time transaction is closed without a loanId',
          status,
          buildErrorModel(
            translatedText('payOverTimeErrors.unavailableTitle'),
            translatedText('payOverTimeErrors.closedBody'),
          ),
        );
        return;

      default:
        logUnexpectedPayOverTimeState(
          'Pay Over Time transaction has an unhandled status',
          { transactionId, status },
        );
    }
  };

  const handleViewPaymentPlan = async () => {
    setSubmitErrorModel(null);

    const outcome = await submitOnboardingStep({ transactionId });

    // A cancelled submit means nothing happened, so it must not dismiss the sheet. A repeat
    // tap resolves as cancelled while the first submit is still running, and dismissing here
    // would both hide the loading state and cancel that first submit.
    if (outcome.status === PayOverTimeOnboardingStatus.Cancelled) {
      return;
    }

    await dismissApplyNowSheet();

    switch (outcome.status) {
      case PayOverTimeOnboardingStatus.PaymentPlan:
        setPayOverTimePaymentPlanState(outcome.paymentPlan);
        router.navigate({
          pathname: '/pay-over-time/payment-plan',
          params: { transactionId },
        });
        break;
      case PayOverTimeOnboardingStatus.Declined:
        router.navigate('/pay-over-time/application-decline');
        break;
      case PayOverTimeOnboardingStatus.Error:
        setSubmitErrorModel(outcome.errorModel);
        break;
    }
  };

  useViewDidFocusScreenAnalyticsEvent(
    transactionR2?.type && accountType
      ? {
          key: 'transactionDetails',
          customAttributes: {
            category:
              accountType === AccountProductEnum.ChargeCard
                ? 'varo_believe'
                : (accountType as string)?.toLowerCase(),
            account_type: (accountType as string)?.toLowerCase(),
            merchant_name: (transactionR2 as ChargeCardTransaction)?.merchant
              ?.name,
            mcc_description: transactionR2?.description as string,
            amount: transactionR2?.value.amount as string,
            direction: (transactionR2?.direction as string)?.toLowerCase(),
            transaction_type: (transactionR2?.type as string)?.toLowerCase(),
            status: (transactionR2?.status as string)?.toLowerCase(),
          },
        }
      : null,
  );

  useEffect(() => {
    if (transactionCancelled) {
      reload();
    }
  }, [reload, transactionCancelled]);

  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [isCancelErrorDismissed, setIsCancelErrorDismissed] = useState(false);

  const handleCancelTransactionPress = () => {
    setCancelModalVisible(true);
  };

  const handleConfirmCancelTransaction = () => {
    setIsCancelErrorDismissed(false);
    void cancelTransaction((transactionR2 as V2ATransaction)?.transferId);
    setCancelModalVisible(false);
  };

  const dismissCancelTransactionError = () => setIsCancelErrorDismissed(true);

  const leaveTransactionDetails = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      dismissToTab(Tabs.HOME);
    }
  };

  const resolveErrorConfig = (): TransactionDetailsErrorConfig | null => {
    if (error) {
      return {
        error,
        messageHeadline: defaultTitle,
        messageBody: resolveErrorCopy(error.message, defaultMessage),
        primaryButton: {
          title: translatedText('errorActions.done'),
          onPress: leaveTransactionDetails,
        },
      };
    }

    if (cancelTransactionError && !isCancelErrorDismissed) {
      const cancelError =
        cancelTransactionError as CancelV2ATransferResultError;

      return {
        error: cancelTransactionError,
        messageHeadline: resolveErrorCopy(cancelError.title, defaultTitle),
        messageBody: resolveErrorCopy(cancelError.message, defaultMessage),
        primaryButton: {
          title: translatedText('errorActions.done'),
          onPress: dismissCancelTransactionError,
        },
      };
    }

    if (submitErrorModel) {
      return {
        error: submitErrorModel,
        messageHeadline: resolveErrorCopy(
          submitErrorModel.messageHeadline,
          defaultTitle,
        ),
        messageBody: resolveErrorCopy(
          submitErrorModel.messageBody,
          defaultMessage,
        ),
        primaryButton: {
          title: translatedText('errorActions.done'),
          onPress: dismissSubmitError,
        },
      };
    }

    if (payOverTimeError) {
      return {
        error: payOverTimeError,
        messageHeadline: payOverTimeError.messageHeadline,
        messageBody: payOverTimeError.messageBody,
        primaryButton: {
          title: translatedText('errorActions.done'),
          onPress: dismissPayOverTimeError,
        },
      };
    }

    return null;
  };

  const errorConfig = resolveErrorConfig();
  const isErrorVisible = Boolean(errorConfig);

  return (
    <View style={styles.mainContainer}>
      <ScreenTransitionOverlay
        isLoading={loading || cancelling}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorConfig ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              error={errorConfig.error}
              messageHeadline={errorConfig.messageHeadline}
              messageBody={errorConfig.messageBody}
              primaryButton={errorConfig.primaryButton}
            />
          ) : null
        }
      />

      <Stack.Screen
        options={{
          title: translatedText('title'),
          headerBackVisible: true,
          headerShown: !isErrorVisible,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
          contentStyle: {
            backgroundColor: theme.color.background.primary,
          },
        }}
      />

      <ScrollView
        style={styles.mainContainer}
        contentContainerStyle={styles.scrollContainer}
      >
        <Dialog
          messageText={translatedText('cancelDialog.confirmationText')}
          isDialogVisible={cancelModalVisible}
          setModalVisible={setCancelModalVisible}
          dialogButtonProps={[
            {
              label: translatedText('cancelDialog.yes'),
              onClick: handleConfirmCancelTransaction,
            },
            {
              label: translatedText('cancelDialog.no'),
              onClick: () => setCancelModalVisible(false),
            },
          ]}
        />

        <View>
          <View style={styles.balanceContainer}>
            <ExpressiveSmText>
              {transactionR2?.value.formattedWithPlusPrefix}
            </ExpressiveSmText>
          </View>

          {payOverTimeStatus === PayOverTimeStatus.Eligible ||
          payOverTimeStatus === PayOverTimeStatus.Active ? (
            <PayOverTimeListItem
              transactionId={transactionId}
              status={payOverTimeStatus}
              onPress={handlePayOverTimePress}
              isLoading={fetchingIntroDetails}
            />
          ) : null}

          <View style={styles.divider} />

          {transactionR2?.type !== TransactionType.Check &&
          transactionR2?.type !== TransactionType.Rdc ? (
            <>
              {transactionR2?.detailsR2 ? (
                <TransactionItems
                  items={transactionR2.detailsR2}
                  type={transactionR2.type}
                />
              ) : null}
              {transactionR2?.caption ? (
                <View style={styles.captionWrapper}>
                  <HairLineDivider />
                  <BodySmText style={styles.captionText}>
                    {transactionR2.caption}
                  </BodySmText>
                </View>
              ) : null}
            </>
          ) : (
            <RdcTransactionDetails
              transaction={transactionR2 as RdcTransaction}
            />
          )}

          {payOverTimeStatus === PayOverTimeStatus.Closed ? (
            <>
              <View style={styles.dividerView}>
                <HairLineDivider />
              </View>
              <PayOverTimeListItem
                transactionId={transactionId}
                status={payOverTimeStatus}
                onPress={handlePayOverTimePress}
                isLoading={fetchingIntroDetails}
              />
            </>
          ) : null}
        </View>
        <View>
          {transactionR2?.type === TransactionType.V2A ? (
            <View style={styles.buttonsWrapper}>
              {((transactionR2 as V2ATransaction)?.isCancellable ?? false) ? (
                <Button
                  label={translatedText('buttons.cancelTransaction')}
                  type={ButtonType.Primary}
                  onPress={handleCancelTransactionPress}
                />
              ) : null}
            </View>
          ) : null}
        </View>
      </ScrollView>

      {payOverTimeStatus === PayOverTimeStatus.Eligible ? (
        <PayOverTimeApplyNowBottomSheet
          ref={bottomSheetRef}
          onViewPaymentPlan={handleViewPaymentPlan}
          onIsOpenChange={handleApplyNowSheetIsOpenChange}
          fetchingSubmitStatus={fetchingSubmitStatus}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  balanceContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.xs,
    padding: theme.spacing.l,
  },
  buttonsWrapper: {
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  captionText: {
    marginVertical: theme.spacing.m,
  },
  captionWrapper: {
    marginHorizontal: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
  divider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  dividerView: {
    marginHorizontal: theme.spacing.m,
  },
  mainContainer: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
});

export default TransactionDetails;
